import useSWR, { mutate } from 'swr';
import { useMemo } from 'react';

// ==============================|| API MENU - MOCK DATA ||============================== //

interface MenuMasterItem {
  id: string;
  children: MenuMasterItem[];
  [key: string]: any;
}

interface MenuMasterData {
  id: string;
  children: MenuMasterItem[];
  items: any[];
  isDashboardDrawerOpened: boolean;
  collapseAllMenus: boolean;
}

interface MenuMasterHook {
  menuMaster: MenuMasterData | undefined;
  menuMasterLoading: boolean;
}

const SESSION_DRAWER_KEY = 'syn:dashboard:drawerOpen';

function readStoredDrawerOpen(): boolean | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const v = sessionStorage.getItem(SESSION_DRAWER_KEY);
    if (v === null) return undefined;
    if (v === 'true' || v === '1') return true;
    if (v === 'false' || v === '0') return false;
  } catch {
    /* ignore quota / private mode */
  }
  return undefined;
}

function persistDrawerOpen(isOpen: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(SESSION_DRAWER_KEY, isOpen ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}

function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    // Alineado con breakpoint `lg` de MUI (down('lg') ~ <1200px).
    return !!window.matchMedia?.('(max-width: 1199.95px)')?.matches;
  } catch {
    return false;
  }
}

const initialState: MenuMasterData = {
  id: 'default',
  children: [],
  items: [],
  isDashboardDrawerOpened: true,
  collapseAllMenus: false
};

function getInitialMenuMaster(): MenuMasterData {
  const stored = readStoredDrawerOpen();
  return {
    ...initialState,
    // En móvil arrancar siempre cerrado.
    isDashboardDrawerOpened: isMobileViewport() ? false : (stored !== undefined ? stored : true)
  };
}

const endpoints = {
  key: 'api/menu',
  master: 'master',
  dashboard: '/dashboard' // server URL
};

export function useGetMenuMaster(): MenuMasterHook {
  const { data, isLoading } = useSWR(endpoints.key + endpoints.master, () => getInitialMenuMaster(), {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const memoizedValue = useMemo(
    () => ({
      menuMaster: data,
      menuMasterLoading: isLoading
    }),
    [data, isLoading]
  );

  return memoizedValue;
}

export function handlerDrawerOpen(isDashboardDrawerOpened: boolean): void {
  persistDrawerOpen(isDashboardDrawerOpened);
  mutate(
    endpoints.key + endpoints.master,
    (currentMenuMaster: MenuMasterData | undefined) => {
      if (!currentMenuMaster) {
        return {
          ...getInitialMenuMaster(),
          isDashboardDrawerOpened,
          collapseAllMenus: !isDashboardDrawerOpened
        };
      }
      return {
        ...currentMenuMaster,
        isDashboardDrawerOpened,
        collapseAllMenus: !isDashboardDrawerOpened
      };
    },
    false
  );
}
